import { computed, onBeforeUnmount, ref, watch } from "vue";
import { formatDateText } from "@/services/useDateTextFormatter";
import { currentLocale } from "@/services/useLocales";
import i18n from "@/plugins/vue-i18n";
import { isDisabledAction, isPendingDisabledAction } from "@/services/disabledActions";

const DISABLE_CHECK_INTERVAL = 1000;
const DISABLED_UNTIL_LOCALE_KEY = "actions.disabledUntil";

const useDisabledActions = actions => {
  const now = ref(Date.now());
  const timer = ref(null);

  const items = computed(() => actions.value || []);

  const isActionDisabled = action => {
    return isDisabledAction(action, now.value);
  };

  const getDisabledActionTooltip = action => {
    if (!isActionDisabled(action) || !action?.disable_until) return null;

    return i18n.t(DISABLED_UNTIL_LOCALE_KEY, currentLocale.value, {
      date: formatDateText(action.disable_until, "dd.MM.yyyy HH:mm"),
    });
  };

  const hasPendingDisabledActions = computed(() => {
    return items.value.some(action => isPendingDisabledAction(action, now.value));
  });

  const startTimer = () => {
    if (timer.value !== null) return;

    timer.value = window.setInterval(() => {
      now.value = Date.now();
    }, DISABLE_CHECK_INTERVAL);
  };

  const stopTimer = () => {
    if (timer.value === null) return;

    window.clearInterval(timer.value);
    timer.value = null;
  };

  watch(
    hasPendingDisabledActions,
    hasPending => {
      if (hasPending) {
        startTimer();
      } else {
        stopTimer();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(stopTimer);

  return {
    isActionDisabled,
    getDisabledActionTooltip,
  };
};

export { useDisabledActions };
